/**
 * Which queries the persisted React Query cache (AsyncStorage / browser
 * storage, kept 30 days) may write to disk.
 */

/** First query-key segment of every announcements query (`@visioni/a3s-notifications/app`). */
const ANNOUNCEMENTS_QUERY_KEY = "announcements";

type PersistableQuery = {
  queryKey: readonly unknown[];
  state: { data: unknown; status: string };
};

const isFeatureResource = (key: unknown, feature: string, resource: string): boolean =>
  typeof key === "object" &&
  key !== null &&
  "feature" in key &&
  "resource" in key &&
  key.feature === feature &&
  key.resource === resource;

export function shouldPersistQuery(query: PersistableQuery): boolean {
  const [key] = query.queryKey;
  // Recipient lists hold customers' email addresses: never written to the device.
  if (key === ANNOUNCEMENTS_QUERY_KEY) return false;
  // Signed URLs expire quickly; selected data must stay JSON-serialisable. Never persist snapshots.
  if (isFeatureResource(key, "media", "snapshots")) return false;
  // Persist successful queries, and failed ones that still hold cached data (offline-first).
  const hasData = query.state.data !== undefined && query.state.data !== null;
  return query.state.status === "success" || (query.state.status === "error" && hasData);
}

type PersistableMutation = {
  options: { mutationKey?: readonly unknown[] | undefined };
  state: { isPaused: boolean };
};

/**
 * Which mutations the persisted cache may write to disk. Like React Query's
 * default, only paused ones (to resume after a restart) — but never an
 * announcements mutation: an upload's variables are a CSV of customers'
 * addresses.
 */
export function shouldPersistMutation(mutation: PersistableMutation): boolean {
  if (mutation.options.mutationKey?.[0] === ANNOUNCEMENTS_QUERY_KEY) return false;
  return mutation.state.isPaused;
}
